import { createTheme, alpha, type Theme } from '@mui/material/styles';

/**
 * PMC design system — admin panel.
 *
 * Shares the citizen portal's palette and typography so the two applications
 * read as one service. Density is tighter here: an officer works through long
 * tables all day, where the citizen sees a handful of rows.
 *
 * The palette is carried over from the legacy portal's stylesheet so the
 * rebuilt portal is recognisably the same service — navy for the utility strip
 * and headings, indigo for interaction. Everything else (spacing, elevation,
 * typography scale) is rebuilt on MUI's system rather than the original's
 * hand-rolled CSS.
 *
 * Devanagari matters here: Inter has no Devanagari glyphs, so Noto Sans
 * Devanagari must follow it in the stack or every Marathi string falls back to
 * a system font and the page loses its typographic identity mid-sentence.
 */

export const PMC = {
  navy: '#0e1b45',
  navyLight: '#1b2c63',
  primary: '#443eae',
  primaryDark: '#37327f',
  primaryLight: '#eef0fa',
  ink: '#1f2937',
  muted: '#64748b',
  line: '#e5e7eb',
  bgSoft: '#f6f7fb',
  success: '#15803d',
  successBg: '#e7f7ed',
  warning: '#b45309',
  warningBg: '#fdf3d7',
  danger: '#b91c1c',
  dangerBg: '#fdeaea',
} as const;

/** Categorical series for the dashboard charts — distinguishable, print-safe. */
export const CHART_COLORS = [
  '#443eae',
  '#0891b2',
  '#7c3aed',
  '#0d9488',
  '#c2410c',
  '#4f46e5',
  '#be123c',
  '#65a30d',
] as const;

const FONT_STACK = [
  'Inter',
  '"Noto Sans Devanagari"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'sans-serif',
].join(', ');

export const theme: Theme = createTheme({
  palette: {
    primary: { main: PMC.primary, dark: PMC.primaryDark, light: PMC.primaryLight },
    secondary: { main: PMC.navy, light: PMC.navyLight },
    success: { main: PMC.success },
    warning: { main: PMC.warning },
    error: { main: PMC.danger },
    text: { primary: PMC.ink, secondary: PMC.muted },
    divider: PMC.line,
    background: { default: PMC.bgSoft, paper: '#ffffff' },
  },

  shape: { borderRadius: 10 },

  typography: {
    fontFamily: FONT_STACK,
    // Toned down from the legacy portal's heavier weights — bold headings
    // and table text read as loud rather than institutional at this density.
    h1: { fontSize: '1.9rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h2: { fontSize: '1.4rem', fontWeight: 600, letterSpacing: '-0.015em' },
    h3: { fontSize: '1.1rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontSize: '1rem', fontWeight: 600 },
    h5: { fontSize: '0.875rem', fontWeight: 500 },
    h6: { fontSize: '0.8rem', fontWeight: 500 },
    subtitle2: { fontSize: '0.72rem', fontWeight: 500, color: PMC.muted },
    body1: { fontSize: '0.85rem' },
    body2: { fontSize: '0.78rem' },
    button: { fontSize: '0.8125rem', textTransform: 'none', fontWeight: 500, letterSpacing: 0 },
    caption: { fontSize: '0.68rem' },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Numeric columns must line up: tabular figures stop amounts from
        // jittering as digits change width.
        '.num': { fontVariantNumeric: 'tabular-nums' },
        '@media print': {
          '.no-print': { display: 'none !important' },
          body: { background: '#fff' },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: PMC.line },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${PMC.line}`,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 8, paddingInline: 18 },
        containedPrimary: {
          '&:hover': { backgroundColor: PMC.primaryDark },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, fontSize: '0.68rem', height: 22 },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: tint(PMC.primary, 0.1),
          fontWeight: 600,
          fontSize: '0.7rem',
          color: PMC.primaryDark,
          whiteSpace: 'nowrap',
        },
        root: { fontSize: '0.78rem', borderColor: PMC.line },
      },
    },

    MuiTextField: { defaultProps: { size: 'small' } },
    MuiSelect: { defaultProps: { size: 'small' } },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, alignItems: 'center' },
        standardInfo: { backgroundColor: PMC.primaryLight },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: PMC.navy, fontSize: '0.7rem' },
      },
    },

    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { fontWeight: 500 } },
    },
  },
});

/** Soft tinted surface for stat tiles and callouts. */
export function tint(color: string, amount = 0.08): string {
  return alpha(color, amount);
}
